import { type Tour, type Step } from 'nextstepjs';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import EmergencyAlert from 'src/components/snackbars/EmergencyAlert';
import {
    MockTrainingUnit,
    MockTrainingResident,
    MockTrainingResidentPendant,
} from 'src/constants/MockTrainingData';
import { getMockRid } from 'src/constants/MockTrainingData';
import { TaskSeverityLevels } from 'src/constants/TaskSeverityLevels';
import { TaskStatuses } from 'src/constants/TaskStatuses';
import { TaskUpdateTypes } from 'src/constants/TaskUpdateTypes';
import { AlertTrainingTours, AlertTrainingElementSelectors } from 'src/constants/Training';
import { selectCurrentUser } from 'src/store/configSlice';
import { Task, CareOutcome } from 'src/store/sageAdminApi';
import {
    setTrainingAlert,
    selectTrainingAlert,
    cancelConfirmation,
    selectIsGuidedTour,
    setIsGuidedTour,
} from 'src/store/trainingSlice';
import { snackActions } from 'src/utils/snackBarTools';

interface useAlertTrainingProps {
    taskRid?: string;
}

export interface CustomStep extends Step {
    showCompletionControls?: boolean;
}

// NextStep Tours Array:
// Multiple tours are used (rather than 1 tour with multiple steps) to better handle route changes between steps
// and to make it easier to keep the tour in sync if the user goes backwards in the flow.
export const AlertTours: Tour[] = [
    {
        tour: AlertTrainingTours.AlertTrainingAlertPage,
        steps: [
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: 'Alert Notification',
                content: 'A resident has pressed their pendant. Tap on the alert to view details.',
                selector: AlertTrainingElementSelectors.AlertCard,
                side: 'bottom',
                showControls: false,
                showSkip: false,
                pointerPadding: 20,
                pointerRadius: 10,
            } as CustomStep,
        ],
    },
    {
        tour: AlertTrainingTours.AlertTrainingClaimAlert,
        steps: [
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: '',
                content: (
                    <>
                        Tap <strong>Claim</strong> when you start providing care to the resident.
                    </>
                ),
                selector: AlertTrainingElementSelectors.AlertFooterButtons,
                side: 'top',
                showControls: false,
                showSkip: false,
                pointerPadding: 30,
                pointerRadius: 10,
            } as CustomStep,
        ],
    },
    {
        tour: AlertTrainingTours.AlertTrainingAlertDetail,
        steps: [
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: '',
                content: 'Here, you can escalate or unclaim an alert.',
                selector: AlertTrainingElementSelectors.OtherActions,
                side: 'bottom',
                showControls: true,
                showSkip: false,
                pointerPadding: 0,
                pointerRadius: 0,
            } as CustomStep,
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: '',
                content:
                    'After providing care to the resident, resolve and confirm you are with them.',
                selector: AlertTrainingElementSelectors.AlertFooterButtons,
                side: 'top',
                showControls: false,
                showSkip: false,
                pointerPadding: 25,
                pointerRadius: 10,
            } as CustomStep,
        ],
    },
    {
        tour: AlertTrainingTours.AlertTrainingSelectMultiOutcome,
        steps: [
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: '',
                content: (
                    <>
                        Select <strong>Bathing</strong> and <strong>Toileting</strong> for practice
                        and tap Select.
                    </>
                ),
                side: 'bottom',
                showControls: true,
                showSkip: false,
                pointerPadding: 0,
                pointerRadius: 0,
            } as CustomStep,
        ],
    },
    {
        tour: AlertTrainingTours.AlertTrainingObservations,
        steps: [
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: '',
                content: (
                    <>
                        Select <strong>new changes</strong> in a residents condition to alert
                        Nurses. Select skip if no change in condition is observed.
                    </>
                ),
                side: 'bottom',
                showControls: true,
                showSkip: false,
                pointerPadding: 0,
                pointerRadius: 0,
            } as CustomStep,
        ],
    },
    {
        tour: AlertTrainingTours.AlertTrainingAddNote,
        steps: [
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: '',
                content: (
                    <>Log how many staff assisted with caring for the resident (including you!).</>
                ),
                side: 'top',
                showControls: true,
                showSkip: false,
                pointerPadding: 0,
                pointerRadius: 0,
            } as CustomStep,
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: '',
                content: (
                    <>
                        Add notes about care provided and changes observed. Tap{' '}
                        <strong>Submit</strong> when you&apos;re done.
                    </>
                ),
                side: 'top',
                showControls: true,
                showSkip: false,
                pointerPadding: 0,
                pointerRadius: 0,
            } as CustomStep,
        ],
    },
    {
        tour: AlertTrainingTours.AlertTrainingCongrats,
        steps: [
            {
                icon: <FontAwesomeIcon icon={faInfoCircle} />,
                title: 'Congratulations!',
                content:
                    'You have successfully completed training for claiming and resolving alerts. You can repeat this training as many times as you need.',
                side: 'top',
                showControls: true,
                showSkip: false,
                pointerPadding: 0,
                pointerRadius: 0,
                showCompletionControls: true,
            } as CustomStep,
        ],
    },
];

function useAlertTraining(props?: useAlertTrainingProps) {
    const taskRid = props?.taskRid;
    const dispatch = useDispatch();
    const trainingAlert = useSelector(selectTrainingAlert);
    const isGuidedTour = useSelector(selectIsGuidedTour);
    const currentUser = useSelector(selectCurrentUser);
    const isTrainingAlert = !!(taskRid && trainingAlert?.id && taskRid === trainingAlert.id);
    const alertTitle = `${MockTrainingUnit.displayName} has reported an emergency`;

    const createTrainingAlert = useCallback(
        (isGuidedTour: boolean = true) => {
            const nowSeconds = Math.floor(Date.now() / 1000);
            const nowMs = Date.now();
            const taskId = getMockRid('task');

            const newAlert = {
                id: taskId,
                title: alertTitle,
                status: TaskStatuses.OpenUnclaimed.id,
                severity: TaskSeverityLevels.Emergency.id,
                updates: [
                    {
                        id: getMockRid('task-update'),
                        type: TaskUpdateTypes.Created,
                        creationTimestamp: nowSeconds.toString(),
                        creationTimestampInMs: nowMs.toString(),
                        author: {
                            device: MockTrainingResidentPendant,
                        },
                    },
                ],
                unit: MockTrainingUnit,
                resident: MockTrainingResident,
                creator: {
                    device: MockTrainingResidentPendant,
                },
                creationTimestamp: nowSeconds.toString(),
                lastUpdateTimestamp: nowSeconds.toString(),
            } as Task;

            dispatch(setTrainingAlert(newAlert));
            dispatch(setIsGuidedTour(isGuidedTour));

            snackActions.error('', {
                autoHideDuration: 15_000,
                content: (key) => <EmergencyAlert id={key} taskId={taskId} message={alertTitle} />,
            });
        },
        [alertTitle, dispatch]
    );

    const claimTrainingAlert = useCallback(() => {
        dispatch(
            setTrainingAlert({
                ...trainingAlert,
                status: TaskStatuses.OpenClaimed.id,
                updates: [
                    ...trainingAlert?.updates!,
                    {
                        id: getMockRid('task-update'),
                        type: TaskUpdateTypes.Claim,
                        creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                        creationTimestampInMs: Date.now().toString(),
                        description: `${currentUser?.firstName} ${currentUser?.lastName} claimed the alert`,
                        author: {
                            staffMember: currentUser,
                        },
                    },
                    {
                        id: getMockRid('task-update'),
                        type: TaskUpdateTypes.StatusChange,
                        creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                        creationTimestampInMs: Date.now().toString(),
                        description: `${currentUser?.firstName} ${currentUser?.lastName} changed the alert status from ${TaskStatuses.OpenUnclaimed.displayName} to ${TaskStatuses.OpenClaimed.displayName}`,
                        author: {
                            staffMember: currentUser,
                        },
                    },
                ],
                assignee: currentUser,
            })
        );
    }, [currentUser, dispatch, trainingAlert]);

    const unClaimTrainingAlert = useCallback(() => {
        dispatch(
            setTrainingAlert({
                ...trainingAlert,
                status: TaskStatuses.OpenUnclaimed.id,
                updates: [
                    ...trainingAlert?.updates!,
                    {
                        id: getMockRid('task-update'),
                        type: TaskUpdateTypes.StatusChange,
                        creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                        creationTimestampInMs: Date.now().toString(),
                        description: `${currentUser?.firstName} ${currentUser?.lastName} changed the alert status from ${TaskStatuses.OpenClaimed.displayName} to ${TaskStatuses.OpenUnclaimed.displayName}`,
                        author: {
                            staffMember: currentUser,
                        },
                    },
                    {
                        id: getMockRid('task-update'),
                        type: TaskUpdateTypes.Unassignment,
                        creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                        creationTimestampInMs: Date.now().toString(),
                        description: `${currentUser?.firstName} ${currentUser?.lastName} unclaimed the alert`,
                        author: {
                            staffMember: currentUser,
                        },
                    },
                ],
                assignee: undefined,
            })
        );
    }, [currentUser, dispatch, trainingAlert]);

    const startConfirmingTrainingAlert = useCallback(() => {
        dispatch(
            setTrainingAlert({
                ...trainingAlert,
                status: TaskStatuses.OpenConfirming.id,
                updates: [
                    ...trainingAlert?.updates!,
                    {
                        id: getMockRid('task-update'),
                        type: TaskUpdateTypes.StatusChange,
                        creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                        creationTimestampInMs: Date.now().toString(),
                        description: `${currentUser?.firstName} ${currentUser?.lastName} changed the alert status from ${TaskStatuses.OpenClaimed.displayName} to ${TaskStatuses.OpenConfirming.displayName}`,
                        author: {
                            staffMember: currentUser,
                        },
                    },
                ],
            })
        );
    }, [currentUser, dispatch, trainingAlert]);

    const cancelConfirmingTrainingAlert = useCallback(() => {
        dispatch(cancelConfirmation());
    }, [dispatch]);

    const confirmTrainingAlert = useCallback(() => {
        dispatch(
            setTrainingAlert({
                ...trainingAlert,
                status: TaskStatuses.OpenConfirmed.id,
                updates: [
                    ...trainingAlert?.updates!,
                    {
                        id: getMockRid('task-update'),
                        type: TaskUpdateTypes.CareConfirmed,
                        creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                        creationTimestampInMs: Date.now().toString(),
                        description: `${currentUser?.firstName} ${currentUser?.lastName} confirmed the alert`,
                        author: {
                            device: MockTrainingResidentPendant,
                        },
                    },
                ],
            })
        );
    }, [currentUser?.firstName, currentUser?.lastName, dispatch, trainingAlert]);

    const resolveTrainingAlert = useCallback(
        (resolutionTypes?: CareOutcome[]) => {
            const resolutionUpdates =
                resolutionTypes?.map((resolutionType) => ({
                    id: getMockRid('task-update'),
                    type: TaskUpdateTypes.ManualEntry,
                    creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                    creationTimestampInMs: Date.now().toString(),
                    description: resolutionType,
                    author: {
                        staffMember: currentUser,
                    },
                })) ?? [];

            dispatch(
                setTrainingAlert({
                    ...trainingAlert,
                    status: TaskStatuses.ClosedResolved.id,
                    updates: [
                        ...trainingAlert?.updates!,
                        ...resolutionUpdates!,
                        {
                            id: getMockRid('task-update'),
                            type: TaskUpdateTypes.Closed,
                            creationTimestamp: Math.floor(Date.now() / 1000).toString(),
                            creationTimestampInMs: Date.now().toString(),
                            description: `${currentUser?.firstName} ${currentUser?.lastName} marked as resolved`,
                            author: {
                                staffMember: currentUser,
                            },
                        },
                    ],
                })
            );
        },
        [currentUser, dispatch, trainingAlert]
    );

    const addNoteToTrainingAlert = useCallback(
        (notes: string) => {
            dispatch(
                setTrainingAlert({
                    ...trainingAlert,
                    notes: [
                        ...(trainingAlert?.notes || []),
                        {
                            id: getMockRid('note'),
                            content: notes,
                            subject: 'Task Note',
                            creator: {
                                staffMember: currentUser,
                            },
                        },
                    ],
                })
            );
        },
        [currentUser, dispatch, trainingAlert]
    );

    const removeTrainingAlert = () => {
        dispatch(setTrainingAlert(undefined));
    };

    return {
        trainingAlert: trainingAlert as Task,
        isTrainingAlert,
        isGuidedTour,
        startConfirmingTrainingAlert,
        cancelConfirmingTrainingAlert,
        confirmTrainingAlert,
        resolveTrainingAlert,
        createTrainingAlert,
        claimTrainingAlert,
        unClaimTrainingAlert,
        addNoteToTrainingAlert,
        removeTrainingAlert,
    };
}

export default useAlertTraining;
